import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Navigation, Clock, Route, GripVertical } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const pickupIcon = L.divIcon({
  className: '',
  html: `<div style="background:#16a34a;width:32px;height:32px;border-radius:50%;border:3px solid white;box-shadow:0 2px 10px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;cursor:grab">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><circle cx="12" cy="12" r="4"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const dropoffIcon = L.divIcon({
  className: '',
  html: `<div style="background:#dc2626;width:32px;height:32px;border-radius:50%;border:3px solid white;box-shadow:0 2px 10px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;cursor:grab">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const PARISHES = [
  'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
];

async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`,
      { headers: { 'User-Agent': 'LiantiaoDeliveryApp/1.0' } }
    );
    const data = await res.json();
    if (data?.address) {
      const addr = data.address;
      const street = addr.road || addr.pedestrian || addr.hamlet || addr.neighbourhood || '';
      const city = addr.city || addr.town || addr.village || addr.suburb || '';
      let parish = 'Kingston';
      const state = addr.state || addr.county || addr.state_district || '';
      for (const p of PARISHES) {
        if (state.toLowerCase().includes(p.toLowerCase()) ||
            (data.display_name || '').toLowerCase().includes(p.toLowerCase())) {
          parish = p;
          break;
        }
      }
      return { street, city, parish, display: data.display_name };
    }
  } catch (err) {
    console.error('Reverse geocode error:', err);
  }
  return null;
}

async function fetchOSRMRoute(pickupCoords, dropoffCoords) {
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${pickupCoords.lng},${pickupCoords.lat};${dropoffCoords.lng},${dropoffCoords.lat}?overview=full&geometries=geojson`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.code === 'Ok' && data.routes?.length > 0) {
      const route = data.routes[0];
      return {
        geometry: route.geometry,
        distance: route.distance,
        duration: route.duration,
      };
    }
  } catch (err) {
    console.error('OSRM route error:', err);
  }
  return null;
}

export default function RideMapPreview({
  pickup,
  dropoff,
  onPickupDrag,
  onDropoffDrag,
  height = 320,
  draggable = true,
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const fallbackLineRef = useRef(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);

  // Store callbacks in refs to avoid re-render loops
  const onPickupDragRef = useRef(onPickupDrag);
  const onDropoffDragRef = useRef(onDropoffDrag);
  useEffect(() => { onPickupDragRef.current = onPickupDrag; }, [onPickupDrag]);
  useEffect(() => { onDropoffDragRef.current = onDropoffDrag; }, [onDropoffDrag]);

  const hasPickup = pickup?.coordinates?.lat && pickup?.coordinates?.lng;
  const hasDropoff = dropoff?.coordinates?.lat && dropoff?.coordinates?.lng;

  // Stable coordinate values for dependency tracking
  const pLat = hasPickup ? Number(pickup.coordinates.lat) : null;
  const pLng = hasPickup ? Number(pickup.coordinates.lng) : null;
  const dLat = hasDropoff ? Number(dropoff.coordinates.lat) : null;
  const dLng = hasDropoff ? Number(dropoff.coordinates.lng) : null;
  const pStreet = pickup?.street || '';
  const dStreet = dropoff?.street || '';

  useEffect(() => {
    if (!mapRef.current || (pLat === null && dLat === null)) return;

    // Initialize map once
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        zoomControl: false,
        attributionControl: false,
        dragging: true,
        scrollWheelZoom: true,
        touchZoom: true,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
      }).addTo(mapInstanceRef.current);
      L.control.zoom({ position: 'topright' }).addTo(mapInstanceRef.current);
    }

    const map = mapInstanceRef.current;

    // Clear old layers
    [pickupMarkerRef, dropoffMarkerRef, routeLayerRef, fallbackLineRef].forEach(ref => {
      if (ref.current) { map.removeLayer(ref.current); ref.current = null; }
    });

    const bounds = [];

    // Drag handler (uses refs for stable callback)
    const handleDragEnd = async (type, latlng) => {
      const result = await reverseGeocode(latlng.lat, latlng.lng);
      const loc = {
        street: result?.street || '',
        city: result?.city || '',
        parish: result?.parish || 'Kingston',
        coordinates: { lat: latlng.lat, lng: latlng.lng },
        fullAddress: result?.display || `${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`,
      };
      if (type === 'pickup') onPickupDragRef.current?.(loc);
      else onDropoffDragRef.current?.(loc);
    };

    if (pLat !== null && pLng !== null) {
      const canDrag = draggable && !!onPickupDragRef.current;
      const marker = L.marker([pLat, pLng], { icon: pickupIcon, draggable: canDrag })
        .addTo(map)
        .bindTooltip(pStreet || 'Pickup', { permanent: false, direction: 'top', offset: [0, -18] });
      if (canDrag) marker.on('dragend', e => handleDragEnd('pickup', e.target.getLatLng()));
      pickupMarkerRef.current = marker;
      bounds.push([pLat, pLng]);
    }

    if (dLat !== null && dLng !== null) {
      const canDrag = draggable && !!onDropoffDragRef.current;
      const marker = L.marker([dLat, dLng], { icon: dropoffIcon, draggable: canDrag })
        .addTo(map)
        .bindTooltip(dStreet || 'Destination', { permanent: false, direction: 'top', offset: [0, -18] });
      if (canDrag) marker.on('dragend', e => handleDragEnd('dropoff', e.target.getLatLng()));
      dropoffMarkerRef.current = marker;
      bounds.push([dLat, dLng]);
    }

    // Fetch road route
    if (pLat !== null && pLng !== null && dLat !== null && dLng !== null) {
      fallbackLineRef.current = L.polyline(bounds, {
        color: '#d4d4d8', weight: 2, dashArray: '6, 8', opacity: 0.5,
      }).addTo(map);

      setRouteLoading(true);
      setRouteInfo(null);

      fetchOSRMRoute({ lat: pLat, lng: pLng }, { lat: dLat, lng: dLng }).then(route => {
        setRouteLoading(false);
        if (route?.geometry) {
          if (fallbackLineRef.current) { map.removeLayer(fallbackLineRef.current); fallbackLineRef.current = null; }
          const coords = route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
          routeLayerRef.current = L.polyline(coords, {
            color: '#f59e0b', weight: 5, opacity: 0.9, lineCap: 'round', lineJoin: 'round',
          }).addTo(map);
          map.fitBounds(routeLayerRef.current.getBounds(), { padding: [50, 50], maxZoom: 15 });
          setRouteInfo({
            distance: (route.distance / 1000).toFixed(1),
            duration: Math.round(route.duration / 60),
          });
        }
      });
    } else {
      setRouteInfo(null);
    }

    // Fit bounds
    if (bounds.length === 2) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    } else if (bounds.length === 1) {
      map.setView(bounds[0], 14);
    }

    setTimeout(() => map.invalidateSize(), 100);
  }, [pLat, pLng, dLat, dLng, pStreet, dStreet, draggable]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, []);

  if (pLat === null && dLat === null) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="ride-map-preview">
      {/* Header */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <Navigation className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-semibold text-gray-700">Route Preview</span>
        {routeInfo && (
          <div className="ml-auto flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 text-gray-600" data-testid="route-distance">
              <Route className="w-3.5 h-3.5 text-amber-500" />
              {routeInfo.distance} km
            </span>
            <span className="flex items-center gap-1 text-gray-600" data-testid="route-duration">
              <Clock className="w-3.5 h-3.5 text-amber-500" />
              ~{routeInfo.duration} min
            </span>
          </div>
        )}
        {routeLoading && (
          <span className="ml-auto text-xs text-gray-400 animate-pulse">Loading route...</span>
        )}
      </div>

      {/* Map */}
      <div
        ref={mapRef}
        className="w-full"
        style={{ height: `${height}px` }}
        data-testid="ride-map-container"
      />

      {/* Legend + drag hint */}
      <div className="px-4 py-2.5 flex items-center justify-between border-t border-gray-100">
        <div className="flex items-center gap-4 text-xs">
          {pLat !== null && (
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-green-600" />
              <span className="text-gray-600 truncate max-w-[140px]">{pStreet || 'Pickup'}</span>
            </div>
          )}
          {dLat !== null && (
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-600" />
              <span className="text-gray-600 truncate max-w-[140px]">{dStreet || 'Destination'}</span>
            </div>
          )}
        </div>
        {draggable && (onPickupDrag || onDropoffDrag) && (
          <span className="text-xs text-gray-400 flex items-center gap-1" data-testid="drag-hint">
            <GripVertical className="w-3 h-3" />
            Drag pins to adjust
          </span>
        )}
      </div>
    </div>
  );
}
